import React, {useState, useEffect} from 'react'
import {Modal, Button, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useUpdateVoiceMutation} from '../../../../../services/cellcast.v2'
import {VoiceItem} from '../type'
import {ContactData} from '../../../../../services/cellcast.v2.types'
import {useNavigate} from 'react-router-dom'

type Props = {
  show: boolean
  record: VoiceItem | null
  contact: ContactData | undefined
  onClose: () => void
  onSaved?: () => void
}

const EditMessageModal: React.FC<Props> = ({show, record, contact, onClose, onSaved}) => {
  const navigate = useNavigate()
  const [updateVoice, {isLoading: isUpdating}] = useUpdateVoiceMutation()

  const [welcomeMessage, setWelcomeMessage] = useState('')
  const [openers, setOpeners] = useState<string[]>([])

  useEffect(() => {
    if (record) {
      setWelcomeMessage(record.welcomeMessage || '')
      setOpeners(Array.isArray(record.nextCallOpeners) ? [...record.nextCallOpeners] : [])
    }
  }, [record])

  const handleAddOpener = () => {
    setOpeners([...openers, ''])
  }

  const handleRemoveOpener = (index: number) => {
    setOpeners(openers.filter((_, i) => i !== index))
  }

  const handleOpenerChange = (index: number, value: string) => {
    const newOpeners = [...openers]
    newOpeners[index] = value
    setOpeners(newOpeners)
  }

  const handleCopyOpener = (opener: string) => {
    if (opener) {
      navigator.clipboard.writeText(opener).then(() => {
        toast.success('Copied to clipboard!')
      })
    }
  }

  const handleCopyWelcomeMessage = () => {
    if (welcomeMessage) {
      navigator.clipboard.writeText(welcomeMessage).then(() => {
        toast.success('Copied to clipboard!')
      })
    }
  }

  const handleSave = async () => {
    if (!record?.id) {
      toast.error('Voice record ID is missing')
      return
    }

    try {
      await updateVoice({
        id: record.id,
        transcript: record.transcript,
        summary: record.summary,
        highlights: record.highlights,
        actionItems: record.actionItems,
        sentiment: record.sentiment,
        leadIntent: record.leadIntent,
        status: record.status,
        welcomeMessage: welcomeMessage,
        nextCallOpeners: openers,
      }).unwrap()

      toast.success('Saved successfully!')
      if (onSaved) onSaved()
      onClose()
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to save changes')
    }
  }

  const handleSendToInbox = () => {
    if (contact?.phone) {
      onClose()
      navigate({pathname: '/inbox', search: `?receiver=${contact.phone}`})
    }
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' className='edit-message-modal'>
      <Modal.Header closeButton>
        <Modal.Title>Edit Welcome Message & Openers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {contact && (
          <div className='mb-4'>
            <label className='fw-bold me-2'>Contact:</label>
            <span>{contact.fullName}</span>
          </div>
        )}

        <Form.Group className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <Form.Label className='fw-bold mb-0'>Welcome Message</Form.Label>
            {welcomeMessage && (
              <Button
                variant='light-primary'
                size='sm'
                onClick={handleCopyWelcomeMessage}
                title='Copy welcome message'
              >
                <i className='bi bi-clipboard me-1'></i>
                Copy
              </Button>
            )}
          </div>
          <Form.Control
            as='textarea'
            rows={6}
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            placeholder='Enter welcome message...'
            className='form-control-solid'
          />
        </Form.Group>

        <div className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <Form.Label className='fw-bold mb-0'>Openers</Form.Label>
            {openers.length === 0 && (
              <Button variant='light-primary' size='sm' onClick={handleAddOpener}>
                <i className='bi bi-plus-circle me-1'></i>
                Add Opener
              </Button>
            )}
          </div>
          {openers.length === 0 ? (
            <div className='text-muted text-center py-4'>
              <p>No openers added yet. Click "Add Opener" to get started.</p>
            </div>
          ) : (
            <div className='openers-list' style={{maxHeight: '300px', overflowY: 'auto'}}>
              {openers.map((opener, index) => (
                <div key={index} className='d-flex gap-2 mb-3 align-items-start'>
                  <Form.Control
                    as='textarea'
                    rows={2}
                    value={opener}
                    onChange={(e) => handleOpenerChange(index, e.target.value)}
                    placeholder={`Opener ${index + 1}...`}
                    className='form-control-solid flex-grow-1'
                  />
                  <Button
                    variant='light-primary'
                    size='sm'
                    onClick={() => handleCopyOpener(opener)}
                    className='mt-0'
                    title='Copy this opener'
                  >
                    <i className='bi bi-clipboard'></i>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={isUpdating}>
          Close
        </Button>
        <Button
          variant='primary'
          onClick={handleSendToInbox}
          disabled={isUpdating || !contact?.phone}
        >
          Send to Inbox
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={isUpdating || !record}>
          {isUpdating ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EditMessageModal

